import { useRef, useState } from "react";
import { activateConsultantRosterBatch, previewConsultantRoster } from "../api";
import { parseConsultantRosterCsv } from "../csv";
import type { ConsultantRosterPreview, ConsultantRosterRecord } from "../types";

const BATCH_SIZE = 20;

function actionSummary(preview: ConsultantRosterPreview) {
  return preview.plan.reduce(
    (summary, entry) => {
      summary[entry.action] += 1;
      return summary;
    },
    { update_profile: 0, create_profile: 0, provision_directory_identity: 0 }
  );
}

export function ConsultantRosterImport(): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [records, setRecords] = useState<ConsultantRosterRecord[]>([]);
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState<ConsultantRosterPreview | null>(null);
  const [busy, setBusy] = useState<"preview" | "activate" | null>(null);
  const [processed, setProcessed] = useState(0);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  async function loadCsv(file: File) {
    setError(null);
    setPreview(null);
    setProcessed(0);
    setComplete(false);
    try {
      const parsed = parseConsultantRosterCsv(await file.text());
      setRecords(parsed);
      setFileName(file.name);
    } catch (reason) {
      setRecords([]);
      setFileName("");
      setError(reason instanceof Error ? reason.message : "The CSV could not be read.");
    }
  }

  async function runPreview() {
    setBusy("preview");
    setError(null);
    try {
      setPreview(await previewConsultantRoster(records));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Roster preview failed.");
    } finally {
      setBusy(null);
    }
  }

  async function runActivation() {
    if (!preview) return;
    setConfirming(false);
    setBusy("activate");
    setError(null);
    let ordinal = processed;
    try {
      while (ordinal < preview.normalized_count) {
        const result = await activateConsultantRosterBatch(
          records,
          preview.digest,
          ordinal,
          BATCH_SIZE
        );
        if (
          result.digest !== preview.digest ||
          result.total_count !== preview.normalized_count ||
          result.start_ordinal !== ordinal ||
          result.next_ordinal <= ordinal
        ) {
          throw new Error("Roster activation stopped because a batch readback did not match.");
        }
        ordinal = result.next_ordinal;
        setProcessed(ordinal);
        if (result.complete) break;
      }
      if (ordinal !== preview.normalized_count) {
        throw new Error("Roster activation stopped before every consultant was processed.");
      }
      setComplete(true);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Roster activation failed.");
    } finally {
      setBusy(null);
    }
  }

  const summary = preview ? actionSummary(preview) : null;

  return (
    <details className="settings-disclosure consultant-roster" aria-label="Consultant roster">
      <summary className="settings-disclosure__summary">
        <span>
          <strong>Consultant assignment roster</strong>
          <small>Super-admin-only directory onboarding for signal assignment.</small>
        </span>
        <span className="settings-disclosure__summary-meta">
          {complete ? "Activated" : preview ? `${preview.normalized_count} ready` : "CSV required"}
          <span className="settings-disclosure__chevron" aria-hidden="true">
            ⌄
          </span>
        </span>
      </summary>
      <div className="settings-disclosure__body consultant-roster__body">
        <p className="consultant-roster__intro">
          Upload the reviewed CSV with Name, Email address, Practice, and Role columns. Previewing
          does not write data. Activation creates directory-only recipients without sending
          invitations or granting platform access.
        </p>

        <div className="consultant-roster__upload">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            aria-label="Consultant roster CSV"
            disabled={busy !== null}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void loadCsv(file);
            }}
          />
          <button
            type="button"
            className="sx-btn sx-btn-p3"
            disabled={busy !== null}
            onClick={() => inputRef.current?.click()}
          >
            Choose reviewed CSV
          </button>
          <span>{fileName || "No roster selected"}</span>
        </div>

        {records.length > 0 && !preview ? (
          <div className="consultant-roster__actions">
            <span>{records.length} rows loaded locally</span>
            <button
              type="button"
              className="sx-btn sx-btn-p3"
              disabled={busy !== null}
              onClick={() => void runPreview()}
            >
              {busy === "preview" ? "Checking…" : "Preview roster"}
            </button>
          </div>
        ) : null}

        {preview && summary ? (
          <div className="consultant-roster__preview" aria-live="polite">
            <div>
              <strong>{preview.normalized_count} consultants verified</strong>
              <span>{summary.provision_directory_identity} new directory identities</span>
              <span>{summary.create_profile} existing users needing profiles</span>
              <span>{summary.update_profile} existing profiles to refresh</span>
            </div>
            <code aria-label="Roster digest">{preview.digest}</code>
            <button
              type="button"
              className="sx-cta"
              disabled={busy !== null || complete}
              aria-haspopup="dialog"
              onClick={() => setConfirming(true)}
            >
              {complete ? "Roster activated" : "Activate verified roster"}
            </button>
          </div>
        ) : null}

        {busy === "activate" ? (
          <div className="consultant-roster__progress" role="status">
            <span className="sxsw-progress-dots" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
            Processing {processed} of {preview?.normalized_count ?? records.length}
          </div>
        ) : null}
        {complete ? (
          <p className="consultant-roster__success" role="status">
            Every verified consultant is now available for assignment. No invitation emails were
            sent.
          </p>
        ) : null}
        {error ? (
          <p className="consultant-roster__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      {confirming && preview ? (
        <div className="sx-decision-dialog-backdrop">
          <div
            className="sx-popover sx-decision-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Confirm consultant roster activation"
          >
            <div className="sx-popover-title">Activate {preview.normalized_count} consultants?</div>
            <p className="sx-popover-help">
              This writes directory-only identities and assignment profiles in production. It does
              not email consultants or grant them normal platform access.
            </p>
            <div className="sx-popover-actions">
              <button
                type="button"
                className="sx-btn sx-btn-p3"
                onClick={() => setConfirming(false)}
              >
                Cancel
              </button>
              <button type="button" className="sx-cta" onClick={() => void runActivation()}>
                Activate roster
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </details>
  );
}
